import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CandidateCard } from '@/components/candidates/CandidateCard';
import { CandidateProfile } from '@/components/candidates/CandidateProfile';
import { ScheduleInterviewModal } from '@/components/modals/ScheduleInterviewModal';
import { AssignTaskModal } from '@/components/modals/AssignTaskModal';
import { candidates } from '@/data/mockData';
import type { Candidate } from '@/data/mockData';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Filter, Grid, List } from 'lucide-react';
import { toast } from 'sonner';

const CandidatesPage = () => {
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filteredCandidates = candidates.filter((candidate) => {
    const matchesSearch =
      candidate.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.skills.some((skill) =>
        skill.toLowerCase().includes(searchQuery.toLowerCase())
      );
    const matchesStatus = statusFilter === 'all' || candidate.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewProfile = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setProfileOpen(true);
  };

  const handleShortlist = (candidate: Candidate) => {
    toast.success(`${candidate.name} has been shortlisted!`);
  };

  const handleScheduleInterview = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setProfileOpen(false);
    setInterviewOpen(true);
  };

  const handleAssignTask = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setProfileOpen(false);
    setTaskOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Candidates</h1>
            <p className="text-muted-foreground">
              Browse and manage all candidates from your talent pool.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="px-3 py-1">
              {filteredCandidates.length} candidates
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, title, or skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="shortlisted">Shortlisted</SelectItem>
                <SelectItem value="interviewed">Interviewed</SelectItem>
                <SelectItem value="hired">Hired</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex rounded-lg border border-border">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Candidates Grid/List */}
        <div
          className={
            viewMode === 'grid'
              ? 'grid gap-4 md:grid-cols-2 xl:grid-cols-3'
              : 'space-y-4'
          }
        >
          {filteredCandidates.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              onViewProfile={handleViewProfile}
              onShortlist={handleShortlist}
              onScheduleInterview={handleScheduleInterview}
            />
          ))}
        </div>

        {filteredCandidates.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-12">
            <p className="text-lg font-medium text-muted-foreground">No candidates found</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <CandidateProfile
        candidate={selectedCandidate}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onShortlist={handleShortlist}
        onScheduleInterview={handleScheduleInterview}
        onAssignTask={handleAssignTask}
      />
      <ScheduleInterviewModal
        candidate={selectedCandidate}
        open={interviewOpen}
        onClose={() => setInterviewOpen(false)}
      />
      <AssignTaskModal
        candidate={selectedCandidate}
        open={taskOpen}
        onClose={() => setTaskOpen(false)}
      />
    </DashboardLayout>
  );
};

export default CandidatesPage;
